import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, 
  CheckCircle2, 
  Clock, 
  Brain,
  Award,
  Code,
  Database,
  Briefcase,
  Palette,
  Microscope,
  LineChart,
  Building,
  Globe,
  Shield,
  Smartphone,
  ArrowRight,
  Target,
  X
} from 'lucide-react';
import Button from '../components/ui/Button';
import SkillAssessmentModal from '../components/questionnaire/SkillAssessmentModal';
import ChallengeExperience from '../components/challenges/ChallengeExperience';

// Simulation data
const simulations = [
  {
    id: 'software-dev',
    title: 'Software Developer',
    description: 'Experience a day in the life of a software developer, from morning standup to code reviews and technical decisions.',
    duration: '15 minutes',
    icon: Code,
    color: 'bg-blue-100 text-blue-600',
    subjects: ['Mathematics', 'Computer Science', 'Statistics'],
    careers: ['Software Engineer', 'Full Stack Developer', 'Mobile App Developer'],
    matchScore: 90,
    questions: [
      {
        id: 'q1',
        text: 'During the morning standup, a teammate mentions they are blocked by a complex bug. What do you do?',
        hint: 'Consider team productivity and knowledge sharing',
        options: [
          {
            id: 'a1',
            text: 'Offer to pair program and help debug the issue',
            score: 10,
            feedback: 'Great choice! Collaboration helps solve problems faster and promotes knowledge sharing.'
          },
          {
            id: 'a2',
            text: 'Suggest they document the bug and move to another task',
            score: 5,
            feedback: 'This is okay, but immediate collaboration might be more effective.'
          },
          {
            id: 'a3',
            text: 'Tell them to figure it out themselves',
            score: 0,
            feedback: 'This misses an opportunity for team collaboration and learning.'
          }
        ]
      }
    ]
  },
  {
    id: 'data-scientist',
    title: 'Data Scientist',
    description: 'Tackle real-world data challenges and make critical decisions about analysis and model development.',
    duration: '20 minutes',
    icon: Database,
    color: 'bg-purple-100 text-purple-600',
    questions: [
      {
        id: 'q1',
        text: 'You discover that 20% of your dataset contains missing values. What is your first step?',
        hint: 'Consider data quality and analysis integrity',
        options: [
          {
            id: 'a1',
            text: 'Analyze the pattern of missing data and consult domain experts',
            score: 10,
            feedback: 'Excellent! Understanding the nature of missing data is crucial.'
          },
          {
            id: 'a2',
            text: 'Simply remove all rows with missing values',
            score: 3,
            feedback: 'This might introduce bias and lose valuable information.'
          },
          {
            id: 'a3',
            text: 'Fill all missing values with zeros',
            score: 1,
            feedback: 'This could significantly skew your analysis.'
          }
        ]
      }
    ]
  },
  {
    id: 'product-manager',
    title: 'Product Manager',
    description: 'Lead product strategy and development, working with cross-functional teams to deliver successful products.',
    duration: '20 minutes',
    icon: Briefcase,
    color: 'bg-green-100 text-green-600',
    questions: [
      {
        id: 'q1',
        text: 'Your team is behind schedule on a major feature release. What do you do?',
        hint: 'Consider project management and stakeholder communication',
        options: [
          {
            id: 'a1',
            text: 'Analyze impact, communicate with stakeholders, and adjust the roadmap',
            score: 10,
            feedback: 'Excellent! Clear communication and strategic planning are key PM skills.'
          },
          {
            id: 'a2',
            text: 'Push the team to work overtime to meet the deadline',
            score: 3,
            feedback: 'This might affect team morale and product quality.'
          },
          {
            id: 'a3',
            text: 'Launch the feature as is with known issues',
            score: 1,
            feedback: 'Rushing features can lead to poor user experience and technical debt.'
          }
        ]
      }
    ]
  },
  {
    id: 'ux-designer',
    title: 'UX Designer',
    description: 'Design intuitive and engaging user experiences for digital products and services.',
    duration: '15 minutes',
    icon: Palette,
    color: 'bg-pink-100 text-pink-600',
    questions: [
      {
        id: 'q1',
        text: "User testing reveals confusion about a new feature. What's your first step?",
        hint: 'Consider user research and design iteration',
        options: [
          {
            id: 'a1',
            text: 'Conduct user interviews to understand specific pain points',
            score: 10,
            feedback: 'Perfect! Direct user feedback is crucial for improving design.'
          },
          {
            id: 'a2',
            text: 'Add more tooltips and documentation',
            score: 4,
            feedback: 'Documentation helps but doesn\'t address core usability issues.'
          },
          {
            id: 'a3',
            text: 'Ignore the feedback as it\'s just a few users',
            score: 0,
            feedback: 'Dismissing user feedback can lead to poor product adoption.'
          }
        ]
      }
    ]
  },
  {
    id: 'cybersecurity',
    title: 'Cybersecurity Analyst',
    description: 'Protect organizations from digital threats and ensure data security.',
    duration: '25 minutes',
    icon: Shield,
    color: 'bg-red-100 text-red-600',
    questions: [
      {
        id: 'q1',
        text: 'You detect unusual network traffic patterns. What\'s your first action?',
        hint: 'Consider security protocols and incident response',
        options: [
          {
            id: 'a1',
            text: 'Isolate affected systems and begin incident response procedures',
            score: 10,
            feedback: 'Excellent! Quick containment is crucial in security incidents.'
          },
          {
            id: 'a2',
            text: 'Monitor the situation without taking action',
            score: 3,
            feedback: 'Delayed response could lead to larger security breaches.'
          },
          {
            id: 'a3',
            text: 'Shut down all network access immediately',
            score: 1,
            feedback: 'This is too disruptive without proper assessment.'
          }
        ]
      }
    ]
  },
  {
    id: 'mobile-dev',
    title: 'Mobile Developer',
    description: 'Create engaging mobile applications for iOS and Android platforms.',
    duration: '20 minutes',
    icon: Smartphone,
    color: 'bg-orange-100 text-orange-600',
    questions: [
      {
        id: 'q1',
        text: 'Users report slow app performance. How do you address this?',
        hint: 'Consider performance optimization and user experience',
        options: [
          {
            id: 'a1',
            text: 'Profile the app and optimize resource-heavy operations',
            score: 10,
            feedback: 'Great! Data-driven optimization is the best approach.'
          },
          {
            id: 'a2',
            text: 'Add loading spinners everywhere',
            score: 3,
            feedback: 'This masks the problem rather than solving it.'
          },
          {
            id: 'a3',
            text: 'Suggest users upgrade their devices',
            score: 0,
            feedback: 'The app should be optimized for a range of devices.'
          }
        ]
      }
    ]
  },
  {
    id: 'data-analyst',
    title: 'Data Analyst',
    description: 'Transform raw data into actionable insights for business decision-making.',
    duration: '20 minutes',
    icon: LineChart,
    color: 'bg-indigo-100 text-indigo-600',
    questions: [
      {
        id: 'q1',
        text: 'You notice an unusual spike in user churn. How do you investigate?',
        hint: 'Consider data analysis methodology',
        options: [
          {
            id: 'a1',
            text: 'Perform a comprehensive cohort analysis',
            score: 10,
            feedback: 'Excellent! This will help identify patterns and potential causes.'
          },
          {
            id: 'a2',
            text: 'Look only at the most recent data',
            score: 4,
            feedback: 'Recent data is important but historical context helps understand trends.'
          },
          {
            id: 'a3',
            text: 'Wait to see if it resolves itself',
            score: 0,
            feedback: 'Proactive investigation is crucial for addressing issues early.'
          }
        ]
      }
    ]
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

const CareerSimulator: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSimulation, setSelectedSimulation] = useState<any | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<string[]>([]);
  const [showSkillAssessment, setShowSkillAssessment] = useState(false);
  const [showChallengeExperience, setShowChallengeExperience] = useState(false);

  const handleStartSimulation = (simulation: any) => {
    setSelectedSimulation(simulation);
    setShowChallengeExperience(true);
  };

  const handleCloseSimulation = () => {
    setSelectedSimulation(null);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
    setFeedback([]);
  };

  const handleAnswer = (questionId: string, answerId: string) => {
    if (!selectedSimulation) return;

    const question = selectedSimulation.questions[currentQuestionIndex];
    const selectedOption = question.options.find(opt => opt.id === answerId);
    
    if (selectedOption?.feedback) {
      setFeedback([...feedback, selectedOption.feedback]);
    }
    
    setAnswers({ ...answers, [questionId]: answerId });
    
    if (currentQuestionIndex < selectedSimulation.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      calculateScore();
    }
  };

  const calculateScore = () => {
    let totalScore = 0;
    let maxScore = 0;

    selectedSimulation.questions.forEach((question: any) => {
      const selectedAnswerId = answers[question.id];
      const selectedAnswer = question.options.find((opt: any) => opt.id === selectedAnswerId);
      const maxQuestionScore = Math.max(...question.options.map((opt: any) => opt.score));
      
      if (selectedAnswer) {
        totalScore += selectedAnswer.score;
      }
      maxScore += maxQuestionScore;
    });

    const percentageScore = (totalScore / maxScore) * 100;
    setScore(percentageScore);
    setShowResults(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Career Reality Check Simulator
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Experience a day in the life of different careers and test your suitability through interactive scenarios.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Brain className="h-5 w-5 mr-2" />
                    <span>Interactive Scenarios</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Target className="h-5 w-5 mr-2" />
                    <span>Real-world Challenges</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Award className="h-5 w-5 mr-2" />
                    <span>Instant Feedback</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {!selectedSimulation && (
            <motion.div
              variants={containerVariants}
              className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {simulations.map((sim) => {
                const Icon = sim.icon;
                return (
                  <motion.div
                    key={sim.id}
                    variants={itemVariants}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow flex flex-col"
                  >
                    <div className="flex items-center mb-4">
                      <div className={`rounded-full p-2 mr-4 ${sim.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">{sim.title}</h2>
                    </div>
                    
                    <p className="text-gray-600 mb-4 flex-grow">{sim.description}</p>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-6">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>{sim.duration}</span>
                    </div>

                    <Button
                      onClick={() => handleStartSimulation(sim)}
                      fullWidth
                      icon={<Play className="h-4 w-4" />}
                      className="mt-auto"
                    >
                      Start Simulation
                    </Button>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {selectedSimulation && !showResults && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                variants={itemVariants}
                className="relative max-w-4xl w-full bg-white rounded-lg shadow-md p-6 md:p-8"
              >
                <button
                  onClick={handleCloseSimulation}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {selectedSimulation.title} Simulation
                    </h2>
                    <span className="text-sm text-gray-500">
                      Question {currentQuestionIndex + 1} of {selectedSimulation.questions.length}
                    </span>
                  </div>
                  
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-primary-600 rounded-full transition-all duration-300"
                      style={{
                        width: `${((currentQuestionIndex + 1) / selectedSimulation.questions.length) * 100}%`
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-4">
                      {selectedSimulation.questions[currentQuestionIndex].text}
                    </h3>
                    {selectedSimulation.questions[currentQuestionIndex].hint && (
                      <p className="text-sm text-gray-500 italic mb-4">
                        Hint: {selectedSimulation.questions[currentQuestionIndex].hint}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    {selectedSimulation.questions[currentQuestionIndex].options.map((option: any) => (
                      <button
                        key={option.id}
                        onClick={() => handleAnswer(selectedSimulation.questions[currentQuestionIndex].id, option.id)}
                        className="w-full text-left p-6 rounded-lg border-2 hover:border-primary-500 hover:bg-primary-50 transition-colors"
                      >
                        {option.text}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {showResults && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                variants={itemVariants}
                className="relative max-w-4xl w-full bg-white rounded-lg shadow-md p-6 md:p-8"
              >
                <button
                  onClick={handleCloseSimulation}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>

                <div className="text-center mb-8">
                  <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
                    <Award className="h-12 w-12 text-primary-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Simulation Complete!
                  </h2>
                  <p className="text-gray-600">
                    Here's how well you align with this career path
                  </p>
                </div>

                <div className="mb-8">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium text-gray-700">Career Suitability Score</span>
                    <span className="font-bold text-primary-600">{score.toFixed(1)}%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full">
                    <div
                      className="h-4 bg-primary-600 rounded-full transition-all duration-1000"
                      style={{ width: `${score}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  {feedback.map((item, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-600">{item}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={handleCloseSimulation}
                    variant="outline"
                    className="mr-4"
                  >
                    Close
                  </Button>
                  <Button onClick={() => navigate('/skills')}>
                    Continue to Skills
                  </Button>
                </div>
              </motion.div>
            </div>
          )}

          <motion.div 
            variants={itemVariants}
            className="mt-8 flex justify-end"
          >
            <Button
              onClick={() => setShowSkillAssessment(true)}
              className="bg-primary-600 text-white hover:bg-primary-700 transition-all duration-300"
              icon={<ArrowRight className="h-4 w-4" />}
              iconPosition="right"
            >
              Try Skill Development
            </Button>
          </motion.div>

          {/* Add Skill Assessment Modal */}
          <SkillAssessmentModal
            isOpen={showSkillAssessment}
            onClose={() => setShowSkillAssessment(false)}
          />

          {/* Add Challenge Experience Modal */}
          {selectedSimulation && (
            <ChallengeExperience
              isOpen={showChallengeExperience}
              onClose={() => {
                setShowChallengeExperience(false);
                setSelectedSimulation(null);
              }}
              challenge={selectedSimulation}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CareerSimulator;